<template>
  <toolbar-container>
    <template #toolbar>
      <reservations-toolbar
        :reservations="reservations"
        @click:waitlist="applyWaitlistFilter"
      >
        <div>
          <div class="text-body-2 align-left pl-0 pl-md-1 text--secondary">
            <!-- Res. / Table / Guest Header shown ;
          sm+: always
          xs: only iff not showing lunch and dinner stats, tables hidden on xs. -->
            <template v-if="!($vuetify.breakpoint.xsOnly && showLunchDinnerStats)">
              <span class="d-block d-sm-inline">{{ $t('label.reservations') }}:
                <span class="font-weight-bold">{{ stats.reservations }}</span>
                <span v-if="$vuetify.breakpoint.smAndUp"> / </span>
              </span>
              <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('label.tables') }}:
                <span class="font-weight-bold">{{ stats.tables }}</span>
                <span v-if="$vuetify.breakpoint.smAndUp"> / </span>
              </span>
              <span class="d-block d-sm-inline">{{ $t('label.guests') }}:
                <span class="font-weight-bold">{{ stats.guests }}</span>
              </span>
            </template>
          </div>
          <!-- /// -->

          <div
            v-if="showLunchDinnerStats"
            class="text-body-2 align-left pl-0 pl-md-1 text--secondary pb-0"
          >
            {{ $t('label.lunch_dinner') }}
            <span class="font-weight-bold">{{ stats.lunch }}</span> /
            <span class="font-weight-bold">{{ stats.dinner }}</span>
          </div>
        <!-- /// -->
        </div>
        <template #buttons>
          <v-btn
            icon
            @click="filterBtnClicked"
          >
            <v-icon>
              {{ hasFilter ? 'mdi-filter' : 'mdi-filter-outline' }}
            </v-icon>
          </v-btn>
        </template>
      </reservations-toolbar>
    </template>

    <!-- Filter View Header -->
    <template v-if="filterVisible || hasFilter">
      <guest-filter
        :compact="$vuetify.breakpoint.xsOnly"
        :visible="filterVisible"
        @close="filterVisible = false"
      />
    </template>
    <template v-if="$vuetify.breakpoint.xsOnly && hasFilter">
      <guest-filter-compact-bar />
      <v-divider />
    </template>

    <!-- Placeholder - closed  -->
    <guest-list-placeholder
      v-if="showNotOpen"
      :title="$t('placeholder.no_availability')"
    />
    <!-- Placeholder - no reservations -->
    <guest-list-placeholder
      v-else-if="showNoReservation"
      :title="$t('placeholder.no_reservations')"
    />
    <!-- Placeholder - clear filter to show reservation -->
    <guest-list-placeholder
      v-else-if="showClearFilter"
      :title="$t('placeholder.no_matching_reservations')"
      :button-title="$t('button.clear_filter')"
      @click="clearFilter"
    />
    <!-- Placeholder - apply all filter to show reservation -->
    <guest-list-placeholder
      v-else-if="showAllFilter"
      :title="$t('placeholder.no_planned_reservations')"
      :button-title="$t('button.show_all')"
      @click="applyAllFilter"
    />
    <!-- Guest List -->
    <guest-list
      v-else
      :reservations-by-time="reservationsByTime"
    />
  </toolbar-container>
</template>

<script lang="ts">
import Vue from 'vue';
import Reservation, { ReservationStatus } from '@/model/Reservation';
import GuestList from '@/components/views/guests/GuestList.vue';
import GuestFilter from '@/components/views/guests/GuestFilter.vue';
import GuestFilterCompactBar from '@/components/views/guests/GuestFilterCompactBar.vue';
import ReservationsToolbar from '@/components/views/main/ReservationsToolbar.vue';
import KeyboardControl from '@/mixins/keyboard-control';
import GuestListPlaceholder from '@/components/views/guests/GuestListPlaceholder.vue';

enum PlaceholderType { NONE, NOT_LOADED, NO_RESERVATION, NOT_OPEN, CLEAR_FILTER, ALL_FILTER }

export default Vue.extend({
  name: 'Guests',
  components: {
    GuestList,
    ReservationsToolbar,
    GuestFilter,
    GuestFilterCompactBar,
    GuestListPlaceholder,
  },
  mixins: [KeyboardControl],
  data() {
    return {
      filterVisible: false,
    };
  },

  computed: {
    reservations(): Reservation[] {
      return this.$tstore.getters.filteredReservationsNoBlocks;
    },
    reservationsByTime(): Map<string, Reservation[]> {
      return this.$tstore.getters.filteredReservationsNoBlocksByTimeText;
    },
    hasFilter(): boolean {
      return this.$tstore.getters.isFilter;
    },
    stats() {
      return this.$tstore.getters.dayStats;
    },
    showLunchDinnerStats() {
      return this.$tstore.state.settings.accountSettings.showLunchDinner ?? false;
    },
    showNotOpen(): boolean { return this.placeholderType === PlaceholderType.NOT_OPEN; },
    showNoReservation(): boolean { return this.placeholderType === PlaceholderType.NO_RESERVATION; },
    showClearFilter(): boolean { return this.placeholderType === PlaceholderType.CLEAR_FILTER; },
    showAllFilter(): boolean { return this.placeholderType === PlaceholderType.ALL_FILTER; },
    placeholderType() {
      const {
        isDateLoaded, isDayOpen,
        isDayReservationNoBlock, isDayValidForTodayReservationNoBlock, isDayFilteredReservationNoBlock,
      } = this.$tstore.getters;

      if (!isDateLoaded) return PlaceholderType.NOT_LOADED; // day reservation not loaded -> not loaded placehoder
      if (isDayFilteredReservationNoBlock) return PlaceholderType.NONE; // some reservations passed filter -> no p.
      if (isDayValidForTodayReservationNoBlock) return PlaceholderType.CLEAR_FILTER; // some valid res. -> clear f. p.
      if (isDayReservationNoBlock) return PlaceholderType.ALL_FILTER; // some invalid reservations -> all filter p.
      if (!isDayOpen) return PlaceholderType.NOT_OPEN; // not open -> not open placehoder
      return PlaceholderType.NO_RESERVATION; // is open but no reservation -> no reservation placeholder
    },
  },
  watch: {
    hasFilter() {
      if (this.hasFilter) { return; }
      this.filterVisible = false;
    },
  },
  methods: {
    filterBtnClicked() {
      if (this.hasFilter && this.$vuetify.breakpoint.smAndUp) {
        this.$tstore.dispatch('resetFilter');
      } else {
        this.filterVisible = !this.filterVisible;
      }
    },
    clearFilter() {
      this.$tstore.dispatch('resetFilter');
    },
    applyAllFilter() {
      this.$tstore.dispatch('applyAllFilter');
    },
    applyWaitlistFilter() {
      this.$tstore.dispatch('applyFilter', { status: ReservationStatus.WaitingList });
    },
  },
});

</script>
